import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEffect, useState } from "react";
import {
  ENGAGEMENT_STATUSES,
  ENGAGEMENT_STAGES,
  ENGAGEMENT_HEALTHS,
  BILLING_MODELS,
  BILLING_MODEL_LABEL,
} from "@/lib/enumLabels";

const fmt$ = (n) => "$" + Number(n || 0).toLocaleString();

/**
 * Six-card KPI grid at the top of the engagement page.
 * Structural extraction only — same fields, same testids, same behavior.
 *
 * Iter 85.56 · Budget becomes advisor-editable (advisor sets/updates the
 *   engagement budget); Billed to Date becomes read-only (system-derived
 *   from actual billing/invoice activity per firm policy).
 */
export default function EngagementStatusGrid({ engagement: e, onSave, onLocalPatch }) {
  const budgetPct = e.budget ? Math.round((e.billed_to_date || 0) / e.budget * 100) : 0;
  const deliverables = e.deliverables || [];
  const dvDone = deliverables.filter((d) => d.status === "delivered").length;
  const dvPct = deliverables.length ? Math.round((dvDone / deliverables.length) * 100) : 0;

  // Local input state for Budget so the advisor can freely type (including
  // clearing to empty) without the parent's onLocalPatch fighting each
  // keystroke. Committed on blur / Enter.
  const [budgetInput, setBudgetInput] = useState(
    e.budget == null || e.budget === "" ? "" : String(e.budget),
  );
  useEffect(() => {
    setBudgetInput(e.budget == null || e.budget === "" ? "" : String(e.budget));
  }, [e.budget]);

  const commitBudget = () => {
    const raw = budgetInput.trim();
    // Empty string → clear the budget (persist null so the KPI card falls
    // back to $0 like a freshly-scoped engagement). Otherwise coerce to a
    // finite number; ignore NaN so a stray keystroke doesn't wipe the field.
    const next = raw === "" ? null : parseFloat(raw);
    if (raw !== "" && !Number.isFinite(next)) {
      setBudgetInput(e.budget == null ? "" : String(e.budget));
      return;
    }
    if (next === (e.budget ?? null)) return;
    onSave({ budget: next });
  };

  // Iter 85.62 · Default Hourly Rate — mirrors the Budget input pattern.
  // Empty clears to null; NaN is ignored; blur/Enter commits. Historical
  // timesheet entry rates are never rewritten server-side, so changing
  // this affects future entries only.
  const [rateInput, setRateInput] = useState(
    e.default_hourly_rate == null || e.default_hourly_rate === "" ? "" : String(e.default_hourly_rate),
  );
  useEffect(() => {
    setRateInput(e.default_hourly_rate == null || e.default_hourly_rate === "" ? "" : String(e.default_hourly_rate));
  }, [e.default_hourly_rate]);
  const commitRate = () => {
    const raw = rateInput.trim();
    const next = raw === "" ? null : parseFloat(raw);
    if (raw !== "" && !Number.isFinite(next)) {
      setRateInput(e.default_hourly_rate == null ? "" : String(e.default_hourly_rate));
      return;
    }
    if (next === (e.default_hourly_rate ?? null)) return;
    onSave({ default_hourly_rate: next });
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
      <div className="border border-border bg-card p-4">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Status</div>
        <Select value={e.status} onValueChange={(v) => onSave({ status: v })}>
          <SelectTrigger data-testid="engagement-status-select" className="rounded-none bg-transparent border-0 p-0 h-auto font-display text-base font-bold"><SelectValue /></SelectTrigger>
          <SelectContent>{ENGAGEMENT_STATUSES.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="border border-border bg-card p-4">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Stage</div>
        <Select value={e.stage} onValueChange={(v) => onSave({ stage: v })}>
          <SelectTrigger className="rounded-none bg-transparent border-0 p-0 h-auto font-display text-base font-bold"><SelectValue /></SelectTrigger>
          <SelectContent>{ENGAGEMENT_STAGES.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="border border-border bg-card p-4">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Health</div>
        <Select value={e.health} onValueChange={(v) => onSave({ health: v })}>
          <SelectTrigger className="rounded-none bg-transparent border-0 p-0 h-auto font-display text-base font-bold"><SelectValue /></SelectTrigger>
          <SelectContent>{ENGAGEMENT_HEALTHS.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {/* Iter 85.62 · Billing Model now editable inline. Persists on
          canonical Engagement.billing_model; flows into Timesheet /
          Invoice logic via the existing architecture (recompute_engagement_billing). */}
      <div className="border border-border bg-card p-4">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Billing model</div>
        <Select value={e.billing_model || "fixed"} onValueChange={(v) => onSave({ billing_model: v })}>
          <SelectTrigger
            data-testid="engagement-billing-model-select"
            className="rounded-none bg-transparent border-0 p-0 h-auto font-display text-base font-bold"
          >
            <SelectValue>{BILLING_MODEL_LABEL[e.billing_model] || "Fixed fee"}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {BILLING_MODELS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border bg-card p-4">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Budget</div>
        <Input
          type="number"
          data-testid="engagement-budget-input"
          value={budgetInput}
          onChange={(ev) => setBudgetInput(ev.target.value)}
          onBlur={commitBudget}
          onKeyDown={(ev) => {
            if (ev.key === "Enter") { ev.currentTarget.blur(); }
            if (ev.key === "Escape") {
              setBudgetInput(e.budget == null ? "" : String(e.budget));
              ev.currentTarget.blur();
            }
          }}
          placeholder="Set budget"
          className="rounded-none bg-transparent border-0 p-0 h-auto font-display text-lg font-bold data-cell w-full"
        />
        <div className="mt-1 h-1 bg-secondary">
          <div className="h-full" style={{
            width: Math.min(budgetPct, 100) + "%",
            background: budgetPct > 100
              ? "hsl(var(--destructive))"
              : budgetPct > 80
                ? "hsl(var(--brass))"
                : "hsl(var(--chart-2))",
          }} />
        </div>
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-1">{fmt$(e.billed_to_date || 0)} · {budgetPct}%</div>
      </div>
      {/* Iter 85.62 · Default Hourly Rate — prefills every new time entry
          on this engagement. Individual entries can override without
          mutating this field; historical entries are never rewritten. */}
      <div className="border border-border bg-card p-4">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Hourly rate</div>
        <Input
          type="number"
          data-testid="engagement-default-hourly-rate-input"
          value={rateInput}
          onChange={(ev) => setRateInput(ev.target.value)}
          onBlur={commitRate}
          onKeyDown={(ev) => {
            if (ev.key === "Enter") { ev.currentTarget.blur(); }
            if (ev.key === "Escape") {
              setRateInput(e.default_hourly_rate == null ? "" : String(e.default_hourly_rate));
              ev.currentTarget.blur();
            }
          }}
          placeholder="Firm default"
          className="rounded-none bg-transparent border-0 p-0 h-auto font-display text-lg font-bold data-cell w-full"
        />
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-1">
          Prefills time entries
        </div>
      </div>
      <div className="border border-border bg-card p-4" data-testid="deliverable-progress-card">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Deliverables</div>
        <div className="font-display text-lg font-bold data-cell">{dvDone}/{deliverables.length || 0}</div>
        <div className="mt-1 h-1 bg-secondary">
          <div className="h-full" style={{
            width: (deliverables.length ? dvPct : 0) + "%",
            background: dvPct === 100 ? "hsl(var(--brass))" : "hsl(var(--chart-2))",
          }} />
        </div>
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-1" data-testid="deliverable-progress-pct">{deliverables.length ? dvPct : 0}% delivered</div>
      </div>
      <div className="border border-border bg-card p-4">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Billed to date</div>
        <div
          data-testid="engagement-billed-display"
          className="font-display text-lg font-bold data-cell"
        >
          {fmt$(e.billed_to_date || 0)}
        </div>
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-1">System-derived</div>
      </div>
    </div>
  );
}
